import { ORDER_FLOW, type OrderStatus, TRANSITION_ACTOR } from '@ambernord/shared';
import {
  Button,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  SlideOver,
  StatusBadge,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  cn,
  useToast,
} from '@ambernord/ui';
import { LayoutGrid, PackageSearch, Search, Table2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppLayout } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate, productName } from '../lib/format.js';
import { actorKindOf } from './supply-order/types.js';

interface SupplyRow {
  id: string;
  ref: string;
  status: OrderStatus;
  terms?: { productId?: string; qty?: number; unit?: string; notes?: string } | null;
  deliveryWindowStart?: string | null;
  deliveryWindowEnd?: string | null;
  confirmedDeliveryDate?: string | null;
  confirmationDeadline?: string | null;
  shipToProjection?: {
    line1?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  } | null;
}

interface LoadingAddr {
  id: string;
  label: string;
  city: string;
  country: string;
}

interface ProductItem {
  id: string;
  sku: string;
  namesI18n: Record<string, string>;
}

/** Nākamais uz-priekšu statuss (izlaižot `cancelled`). */
function nextForward(status: OrderStatus): OrderStatus | undefined {
  return ORDER_FLOW[status].find((s) => s !== 'cancelled');
}

/** Dzīves cikla fāzes — filtru čipiem un dēļa kolonnām (viena patiesība). */
const PHASES = [
  {
    key: 'offer',
    statuses: ['quote_requested', 'offer_drafted', 'offer_sent', 'offer_approved'],
  },
  { key: 'placed', statuses: ['placed', 'accepted', 'awaiting_confirmation', 'confirmed'] },
  { key: 'production', statuses: ['production', 'labelled', 'loaded'] },
  { key: 'transit', statuses: ['dispatched', 'delivered'] },
  { key: 'closing', statuses: ['inspected', 'invoiced', 'paid'] },
  { key: 'cancelled', statuses: ['cancelled'] },
] as const;

type PhaseKey = (typeof PHASES)[number]['key'];

function phaseOf(status: OrderStatus): PhaseKey {
  return (
    PHASES.find((p) => (p.statuses as readonly OrderStatus[]).includes(status))?.key ?? 'offer'
  );
}

export default function SupplyOrders() {
  const { t, i18n } = useTranslation(['supplyorders', 'statuses']);
  const lang = i18n.language;
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const role = user?.role ?? '';
  const mine = actorKindOf(role);
  const isSupplier = mine === 'supplier';
  // Piegādātājs un pārvadātājs redz vienu un to pašu anonimizēto projekciju (§7).
  const isAnonymized = isSupplier || mine === 'carrier';

  const [items, setItems] = useState<SupplyRow[] | null>(null);
  const [error, setError] = useState(false);
  const [addresses, setAddresses] = useState<LoadingAddr[]>([]);
  const [productMap, setProductMap] = useState<Record<string, Record<string, string>>>({});
  const [busy, setBusy] = useState<string | null>(null);

  // Filtri + skats.
  const [phase, setPhase] = useState<PhaseKey | 'all'>('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'table' | 'board'>('table');

  // Apstiprināšana SlideOver panelī (datums + iekraušanas adrese).
  const [confirmTarget, setConfirmTarget] = useState<SupplyRow | null>(null);
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmAddr, setConfirmAddr] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Iekraušanas adreses pievienošana (kad piegādātājam vēl nav neviena).
  const [addr, setAddr] = useState({
    label: '',
    line1: '',
    city: '',
    postalCode: '',
    country: '',
  });

  const load = useCallback(async () => {
    const res = await api.get<{ items: SupplyRow[] }>('/api/supply-orders');
    if (!res.ok) {
      setError(true);
      return;
    }
    setError(false);
    setItems(res.data.items ?? []);
  }, []);

  const loadAddresses = useCallback(async () => {
    if (!isSupplier) return;
    const res = await api.get<{ items: LoadingAddr[] }>('/api/registry/loading-addresses');
    if (res.ok) setAddresses(res.data.items ?? []);
  }, [isSupplier]);

  // Produktu nosaukumi — labākā piepūle (piegādātājam katalogs var nebūt pieejams).
  const loadProducts = useCallback(async () => {
    const res = await api.get<{ items: ProductItem[] }>('/api/products');
    if (!res.ok) return;
    const map: Record<string, Record<string, string>> = {};
    for (const p of res.data.items ?? []) map[p.id] = p.namesI18n;
    setProductMap(map);
  }, []);

  useEffect(() => {
    void load();
    void loadAddresses();
    void loadProducts();
  }, [load, loadAddresses, loadProducts]);

  async function advance(id: string, toStatus: OrderStatus) {
    setBusy(id);
    try {
      const res = await api.post<{ error?: string }>(`/api/supply-orders/${id}/advance`, {
        toStatus,
      });
      if (!res.ok) {
        toast(res.data.error ?? t('error'), 'error');
        return;
      }
      toast(t('toast.advanced', { status: t(`statuses:${toStatus}`) }));
      await load();
    } finally {
      setBusy(null);
    }
  }

  function openConfirm(o: SupplyRow) {
    setConfirmError(null);
    setConfirmDate('');
    setConfirmAddr('');
    setConfirmTarget(o);
  }

  async function submitConfirm() {
    if (!confirmTarget) return;
    setConfirmError(null);
    if (!confirmDate || !confirmAddr) {
      setConfirmError(t('confirm.missing'));
      return;
    }
    const id = confirmTarget.id;
    setBusy(id);
    try {
      const res = await api.post<{ error?: string }>(`/api/supply-orders/${id}/confirm`, {
        confirmedDeliveryDate: new Date(confirmDate).toISOString(),
        loadingAddressId: confirmAddr,
      });
      if (!res.ok) {
        setConfirmError(res.data.error ?? t('error'));
        return;
      }
      setConfirmTarget(null);
      toast(t('confirm.success'));
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function addAddress() {
    if (!addr.label || !addr.line1 || !addr.city || !addr.postalCode || !addr.country) {
      toast(t('address.missing'), 'error');
      return;
    }
    setBusy('add-addr');
    try {
      const res = await api.post<{ error?: string }>('/api/registry/loading-addresses', addr);
      if (!res.ok) {
        toast(res.data.error ?? t('error'), 'error');
        return;
      }
      setAddr({ label: '', line1: '', city: '', postalCode: '', country: '' });
      toast(t('address.added'));
      await loadAddresses();
    } finally {
      setBusy(null);
    }
  }

  function goToDetail(id: string) {
    navigate(`/supply-orders/${id}`);
  }

  function prodLabel(o: SupplyRow): string {
    const pid = o.terms?.productId;
    if (pid && productMap[pid]) return productName(productMap[pid], lang);
    return o.terms?.notes ?? '—';
  }

  function qtyLabel(o: SupplyRow): string {
    if (o.terms?.qty == null) return '—';
    return `${o.terms.qty} ${o.terms.unit ?? 'kg'}`;
  }

  function deliveryOf(o: SupplyRow): string | null {
    return o.confirmedDeliveryDate ?? o.deliveryWindowEnd ?? o.confirmationDeadline ?? null;
  }

  function renderAction(o: SupplyRow) {
    // 1) Piegādātājs apstiprina (datums + iekraušanas adrese) — SlideOver panelī.
    if (isSupplier && o.status === 'awaiting_confirmation') {
      return (
        <Button size="sm" onClick={() => openConfirm(o)}>
          {t('actions.confirm')}
        </Button>
      );
    }

    // 2) Virzīšana uz nākamo statusu — tikai tā aktora lomai, kam tas atļauts.
    const forward = nextForward(o.status);
    if (!forward) return <span className="text-slate-400 dark:text-slate-500">—</span>;
    const requiredActor = TRANSITION_ACTOR[forward];
    if (requiredActor !== mine) {
      return (
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {t(`waiting.${requiredActor}`)}
        </span>
      );
    }
    return (
      <Button size="sm" disabled={busy === o.id} onClick={() => void advance(o.id, forward)}>
        {busy === o.id ? t('loading') : t('actions.advance', { next: t(`statuses:${forward}`) })}
      </Button>
    );
  }

  const counts = useMemo(() => {
    const c: Record<PhaseKey | 'all', number> = {
      all: items?.length ?? 0,
      offer: 0,
      placed: 0,
      production: 0,
      transit: 0,
      closing: 0,
      cancelled: 0,
    };
    for (const o of items ?? []) c[phaseOf(o.status)] += 1;
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items ?? []).filter(
      (o) =>
        (phase === 'all' || phaseOf(o.status) === phase) &&
        (q === '' || o.ref.toLowerCase().includes(q)),
    );
  }, [items, phase, search]);

  const chip = (key: PhaseKey | 'all', label: string, count: number) => (
    <button
      key={key}
      type="button"
      onClick={() => setPhase(key)}
      aria-pressed={phase === key}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
        phase === key
          ? 'border-brand-600 bg-brand-600 text-white'
          : 'border-slate-300 bg-white text-slate-600 hover:border-brand-400 hover:text-brand-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:text-brand-300',
      )}
    >
      {label}
      <span
        className={cn(
          'rounded-full px-1.5 text-[10px] font-bold',
          phase === key
            ? 'bg-white/20'
            : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
        )}
      >
        {count}
      </span>
    </button>
  );

  const boardCard = (o: SupplyRow) => (
    <button
      key={o.id}
      type="button"
      onClick={() => goToDetail(o.id)}
      className="flex w-full flex-col gap-1.5 rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-colors hover:border-brand-300 hover:shadow dark:border-slate-800 dark:bg-slate-900 dark:hover:border-brand-700"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-slate-900 dark:text-white">{o.ref}</span>
      </div>
      <StatusBadge status={o.status} label={t(`statuses:${o.status}`)} />
      <p className="truncate text-xs text-slate-600 dark:text-slate-300">{prodLabel(o)}</p>
      <div className="flex items-center justify-between text-[11px] text-slate-400 dark:text-slate-500">
        <span>{qtyLabel(o)}</span>
        <span>{formatDate(deliveryOf(o), lang)}</span>
      </div>
    </button>
  );

  const loaded = items !== null;

  return (
    <AppLayout title={t('title')}>
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        {isSupplier
          ? t('intro.supplier')
          : mine === 'carrier'
            ? t('intro.carrier')
            : t('intro.platform')}
      </p>

      {isSupplier && addresses.length === 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-800 dark:bg-amber-950/30">
          <p className="mb-2 font-medium text-amber-800 dark:text-amber-300">
            {t('address.needed')}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {(['label', 'line1', 'city', 'postalCode', 'country'] as const).map((f) => (
              <Input
                key={f}
                className="h-9"
                placeholder={t(`address.${f}`)}
                value={addr[f]}
                onChange={(e) => setAddr((s) => ({ ...s, [f]: e.target.value }))}
              />
            ))}
          </div>
          <Button
            className="mt-2"
            size="sm"
            disabled={busy === 'add-addr'}
            onClick={() => void addAddress()}
          >
            {t('actions.addAddress')}
          </Button>
        </div>
      )}

      {/* Rīkjosla: fāžu čipi + meklēšana + skata pārslēgs. */}
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {chip('all', t('filters.all'), counts.all)}
          {PHASES.map((p) => chip(p.key, t(`phases.${p.key}`), counts[p.key]))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="relative w-full max-w-xs">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <Input
              type="search"
              className="h-9 pl-9"
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t('search')}
            />
          </div>
          <div className="inline-flex rounded-lg border border-slate-300 p-0.5 dark:border-slate-700">
            {(
              [
                ['table', Table2, t('view.table')],
                ['board', LayoutGrid, t('view.board')],
              ] as const
            ).map(([key, Icon, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                aria-pressed={view === key}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                  view === key
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white',
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!loaded && !error && <SkeletonRows rows={6} />}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {t('error')}
        </p>
      )}

      {loaded && !error && items.length === 0 && (
        <EmptyState
          icon={<PackageSearch className="h-6 w-6" aria-hidden />}
          title={t('empty.title')}
          hint={t('empty.hint')}
        />
      )}

      {loaded && !error && items.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon={<PackageSearch className="h-6 w-6" aria-hidden />}
          title={t('empty.noMatches')}
        />
      )}

      {loaded && !error && filtered.length > 0 && view === 'table' && (
        <TableShell>
          <THead>
            <tr>
              <Th>{t('columns.ref')}</Th>
              <Th>{t('columns.product')}</Th>
              <Th>{t('columns.qty')}</Th>
              <Th>{t('columns.shipTo')}</Th>
              <Th>{t('columns.delivery')}</Th>
              <Th>{t('columns.status')}</Th>
              <Th>{t('columns.action')}</Th>
            </tr>
          </THead>
          <tbody>
            {filtered.map((o) => (
              <Tr
                key={o.id}
                interactive
                onClick={() => goToDetail(o.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') goToDetail(o.id);
                }}
              >
                <Td className="font-medium text-slate-900 dark:text-white">{o.ref}</Td>
                <Td>{prodLabel(o)}</Td>
                <Td className="text-slate-500 dark:text-slate-400">{qtyLabel(o)}</Td>
                <Td className="text-slate-500 dark:text-slate-400">
                  {o.shipToProjection
                    ? `${o.shipToProjection.postalCode ?? ''} ${o.shipToProjection.city ?? ''}`.trim()
                    : '—'}
                </Td>
                <Td className="text-slate-500 dark:text-slate-400">
                  {formatDate(deliveryOf(o), lang)}
                </Td>
                <Td>
                  <StatusBadge status={o.status} label={t(`statuses:${o.status}`)} />
                </Td>
                {/* Darbību šūna — klikšķi/taustiņi nedrīkst aizvest uz detaļu lapu. */}
                <Td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  {renderAction(o)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
      )}

      {loaded && !error && filtered.length > 0 && view === 'board' && (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {PHASES.filter((p) => phase === 'all' || p.key === phase).map((p) => {
            const inPhase = filtered.filter((o) => phaseOf(o.status) === p.key);
            return (
              <div
                key={p.key}
                className="flex min-w-[13rem] flex-1 flex-col gap-2 rounded-xl bg-slate-100/70 p-2.5 dark:bg-slate-900/60"
              >
                <p className="flex items-center justify-between px-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t(`phases.${p.key}`)}
                  <span className="rounded-full bg-slate-200 px-1.5 text-[10px] dark:bg-slate-800">
                    {inPhase.length}
                  </span>
                </p>
                {inPhase.length === 0 ? (
                  <p className="px-1 py-3 text-center text-xs text-slate-400 dark:text-slate-600">
                    —
                  </p>
                ) : (
                  inPhase.map(boardCard)
                )}
              </div>
            );
          })}
        </div>
      )}

      {isAnonymized && (
        <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">{t('buyerNote')}</p>
      )}

      {/* Apstiprināšanas panelis — agrāk iestrādāts tabulas šūnā, tagad SlideOver. */}
      <SlideOver
        open={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        title={t('confirm.title', { ref: confirmTarget?.ref ?? '' })}
      >
        <div className="flex flex-col gap-4">
          <Field label={t('confirm.date')} htmlFor="confirm-date">
            <Input
              id="confirm-date"
              type="datetime-local"
              value={confirmDate}
              onChange={(e) => setConfirmDate(e.target.value)}
            />
          </Field>
          <Field label={t('confirm.address')} htmlFor="confirm-addr" error={confirmError}>
            <Select
              id="confirm-addr"
              value={confirmAddr}
              onChange={(e) => setConfirmAddr(e.target.value)}
            >
              <option value="">{t('confirm.addressPlaceholder')}</option>
              {addresses.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} · {a.city}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex gap-2">
            <Button disabled={busy === confirmTarget?.id} onClick={() => void submitConfirm()}>
              {busy === confirmTarget?.id ? t('loading') : t('actions.submit')}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmTarget(null)}>
              {t('actions.cancel')}
            </Button>
          </div>
        </div>
      </SlideOver>
    </AppLayout>
  );
}
